import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Mail, MessageCircle, X } from '../../icons';
import { Avatar } from '../shared/Avatar';
import type { UserSummary } from '../../api/organization';
import { createRoom } from '../../api/chat';
import { getTodayAttendance, type AttendanceDto } from '../../api/attendance';
import { fetchPersonCard, type PersonCardDto } from '../../api/hr-people';
import { useMailComposeStore } from '../../store/mailComposeStore';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { avatarImageUrl } from '../../lib/avatar-src';
import './PersonDetailPopup.css';

/** 트리·검색·상세가 함께 쓰는 한 사람. UserSummary + 소속/직책. */
export interface OrgPerson extends UserSummary {
  divisionName: string | null;
  teamName: string | null;
  /** 팀장 여부에서 온 직책. 팀장이 아니면 null (직책 컬럼은 아직 없음). */
  duty: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  online: '온라인', away: '자리비움', busy: '다른 용무 중', offline: '오프라인',
};

/** 이메일 앞부분 = 로그인 아이디 자리. 별도 loginId 컬럼이 생기면 그쪽으로 바꾼다. */
function loginIdOf(p: OrgPerson): string {
  const at = p.email.indexOf('@');
  return at > 0 ? p.email.slice(0, at) : p.email;
}

/**
 * 대상자 상세 팝업 — 왼쪽 프로필·근무·실행 / 오른쪽 정보.
 *
 * 조직도에서 만든 것을 **채팅 참여자 클릭에서도 같은 모습으로** 쓰려고 꺼냈다.
 * 화면마다 다시 그리면 같은 사람의 정보가 두 모습으로 갈라진다.
 */
export function PersonDetailPopup({
  person, style, placement = 'center', onClose, onNavigated,
}: {
  person: OrgPerson;
  style?: CSSProperties;
  /**
   * 'center'(기본) = 화면 한가운데. 'anchored' = 부르는 쪽이 style 로 left/width 를 준다
   * (조직도는 트리를 덮지 않게 오른쪽으로 밀어 놓는다).
   */
  placement?: 'center' | 'anchored';
  onClose: () => void;
  onNavigated: () => void;
}) {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const requestCompose = useMailComposeStore((s) => s.requestCompose);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [att, setAtt] = useState<AttendanceDto | null>(null);
  /**
   * 사람 카드 (2026-08-28 · 자리 재정의 판 3).
   *
   * ★★ 조직도 응답은 **로그인한 모두에게** 휴대전화·사번을 주고 있었다. 그 두 칸을 조직
   *    응답에서 빼고, 여기서 **범위를 아는 라우트**(`GET /hr/people/:id`)로 다시 받는다 —
   *    본인 · 인사 담당 · **그 사람 팀의 팀장**에게는 보이고, 그 밖에는 안 보인다.
   *    남의 것을 연 것은 그때 기록으로 남는다.
   * ★ 못 받으면(옛 서버 404 · 실패) **넘어온 값 그대로** 쓴다 — 화면이 멎지 않는다.
   */
  const [card, setCard] = useState<PersonCardDto | null>(null);

  const isMe = !!me && String(me.id) === person.id;

  // 중앙에 뜬 팝업은 스스로 닫힌다 — ESC, 그리고 바깥 클릭.
  // 'anchored'(조직도)는 자기 화면이 이미 그 둘을 다루므로 건드리지 않는다.
  const rootRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (placement !== 'center') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && rootRef.current && !rootRef.current.contains(t)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [placement, onClose]);

  // 출근 기록은 **본인 것만** 볼 수 있다(서버 라우트가 본인 전용). 남의 출근 공개는
  // 공개 범위 정책이 정해진 뒤에 여는 것이 맞아 여기서 임의로 뚫지 않는다.
  useEffect(() => {
    if (!isMe) { setAtt(null); return; }
    let alive = true;
    void getTodayAttendance().then((a) => { if (alive) setAtt(a); }).catch(() => { /* 표시만 생략 */ });
    return () => { alive = false; };
  }, [isMe, person.id]);

  useEffect(() => {
    let alive = true;
    setCard(null);
    void fetchPersonCard(person.id)
      .then((c) => { if (alive) setCard(c); })
      .catch(() => { /* 옛 서버 · 실패 — 넘어온 값으로 그린다 */ });
    return () => { alive = false; };
  }, [person.id]);

  const hhmm = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? null
      : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const worked = (() => {
    if (!att?.checkInAt) return null;
    const from = new Date(att.checkInAt).getTime();
    const to = att.checkOutAt ? new Date(att.checkOutAt).getTime() : Date.now();
    const min = Math.floor((to - from) / 60000);
    if (!Number.isFinite(min) || min <= 0) return null;
    /*
      ⛔ **이 자리는 «일부러» 공용 `formatWorkMinutes` 를 안 쓴다** (2026-09-26 결정).
        분을 **두 자리로 채우는 것**이 이 화면의 값이다 — 사람 카드는 여러 사람을 **잇달아**
        여는 자리라 `0시간 05분` / `7시간 30분` 처럼 **자릿수가 맞아야** 눈이 편하다.
        공용 함수로 바꾸면 `5분` / `7시간 30분` 이 되어 길이가 들쭉날쭉해진다(견본 22개 중 19개가 바뀐다).
      ⚠ 그리고 **방금 출근한 사람**(<1시간)이 가장 자주 걸리는 구간이다.
      ⇒ 「여기도 모으면 되겠네」 하고 바꾸지 말 것. 바꾸려면 **정렬을 포기한다는 결정**이 먼저다.
         (정본 = `lib/daily-work.ts` 의 `formatWorkMinutes` · 면제 목록은 `lib/duration-format.test.ts`)
    */
    return `${Math.floor(min / 60)}시간 ${String(min % 60).padStart(2, '0')}분`;
  })();

  const startChat = useCallback(async () => {
    if (busy || isMe) return;
    setBusy(true);
    setActionError(null);
    try {
      const { room } = await createRoom({ type: 'direct', memberIds: [person.id] });
      setActiveChat(room.id);
      onNavigated();
      navigate('/chat');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '채팅방을 열지 못했습니다');
      setBusy(false);
    }
  }, [busy, isMe, person.id, setActiveChat, onNavigated, navigate]);

  const writeMail = useCallback(() => {
    requestCompose({ id: person.id, name: person.name, email: person.email, position: person.position });
    onNavigated();
    navigate('/mail');
  }, [person, requestCompose, onNavigated, navigate]);

  const openCalendar = useCallback(() => {
    onNavigated();
    navigate('/calendar');
  }, [onNavigated, navigate]);

  const belongsPath = [person.divisionName, person.teamName].filter(Boolean).join(' › ');

  /**
   * ★★ **「칸이 없다」와 「값이 없다」를 섞지 않는다** — 서버가 범위 밖 칸은 **키를 아예 빼고**
   *    보낸다. 그래서 `'phone' in card.person` 이 곧 「볼 자격이 있나」다.
   *    자격이 없으면 그 줄을 **아예 안 그린다**(「—」로 두면 «비어 있다» 로 읽힌다).
   */
  const cp = card?.person;
  const has = (k: string) => cp !== undefined && k in cp;

  const rows: Array<[string, string | null | undefined]> = [
    ['아이디', loginIdOf(person)],
    ['이메일', person.email],
    ...(has('phone') ? [['휴대전화', cp!.phone] as [string, string | null | undefined]] : []),
    ...(has('extension') ? [['내선', cp!.extension] as [string, string | null | undefined]] : []),
    ...(has('employeeNumber') ? [['사번', cp!.employeeNumber] as [string, string | null | undefined]] : []),
    ['부서', cp?.divisionName ?? person.divisionName],
    ['팀', cp?.teamName ?? person.teamName],
    ['직책', person.duty],
  ];

  return (
    <aside
      className={`orgdir-detail${placement === 'anchored' ? ' is-anchored' : ''}`}
      style={style}
      role="dialog"
      aria-label={`${person.name} 정보`}
      ref={rootRef}
    >
      <div className="orgdir-detailbar">
        <button type="button" className="orgdir-iconbtn" aria-label="정보 닫기" onClick={onClose}>
          <X size={15} />
        </button>
      </div>

      <div className="orgdir-cols">
        <div className="orgdir-left">
          <div className="orgdir-profile">
            <Avatar
              userId={Number(person.id) || 0}
              name={person.name}
              src={avatarImageUrl(person.id, person.avatarUrl)}
              size="xl"
            />
            <span className="orgdir-dname">{person.name}</span>
            {person.duty && <span className="orgdir-duty">{person.duty}</span>}
            {person.position && <span className="orgdir-dtitle">{person.position}</span>}
            {/* 소속 경로는 있을 때만 — 없으면 줄 자체를 두지 않는다(구분선도 함께 사라진다). */}
            {belongsPath && <span className="orgdir-dpath">{belongsPath}</span>}
          </div>

          <div className="orgdir-att">
            <span className="orgdir-attstate">
              <span className={`orgdir-dot is-${person.status}`} aria-hidden />
              {STATUS_LABEL[person.status] ?? person.status}
            </span>
            {isMe ? (
              <dl className="orgdir-attlist">
                <dt>출근</dt><dd>{hhmm(att?.checkInAt ?? null) ?? '—'}</dd>
                <dt>퇴근</dt><dd>{hhmm(att?.checkOutAt ?? null) ?? '—'}</dd>
                <dt>근무</dt><dd>{worked ?? '—'}</dd>
              </dl>
            ) : (
              <p className="orgdir-attnone">출퇴근 기록은 본인만 볼 수 있습니다.</p>
            )}
          </div>

          <div className="orgdir-acts">
            <button type="button" className="orgdir-act is-primary" onClick={startChat} disabled={busy || isMe}>
              <MessageCircle size={15} aria-hidden />{busy ? '여는 중…' : '채팅하기'}
            </button>
            <button type="button" className="orgdir-act" onClick={writeMail}>
              <Mail size={15} aria-hidden />메일쓰기
            </button>
            <button type="button" className="orgdir-act" onClick={openCalendar}>
              <CalendarDays size={15} aria-hidden />일정보기
            </button>
            {isMe && <p className="orgdir-hint">본인에게는 채팅을 걸 수 없습니다.</p>}
            {actionError && <p className="orgdir-hint orgdir-hint-error" role="alert">{actionError}</p>}
          </div>
        </div>

        <div className="orgdir-right">
          <dl className="orgdir-info">
            {rows.map(([k, v]) => (
              <div key={k} className="orgdir-inforow">
                <dt>{k}</dt>
                <dd>{v || '—'}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </aside>
  );
}
